// src/components/SiteDistancesSection.js
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { getEstadosFromVehiculos, getSitiosByEstado } from '../data/patiosData';
import { 
  MapPin, Search, Loader2, ArrowRight, Route, 
  Info, Target, CheckCircle2
} from 'lucide-react';

import { 
  obtenerDistancias, 
  crearDistancia, 
  actualizarDistancia 
} from '../services/distanciaService';

// helper para generar un id único de par (A-B == B-A)
const makePairKey = (aNombre, bNombre) => {
  const [x, y] = [String(aNombre), String(bNombre)].sort();
  return `${x}::${y}`;
};

const EmptyStateMessage = ({ icon: Icon, title, description }) => (
  <div className="mt-10 flex flex-col items-center justify-center py-12 text-center">
    <div className="flex items-center justify-center w-16 h-16 rounded-full bg-gray-50 border border-dashed border-gray-200 mb-4">
      <Icon className="w-8 h-8 text-gray-300" />
    </div>
    <h4 className="text-base font-semibold text-gray-800">
      {title}
    </h4>
    <p className="mt-1 text-xs text-gray-500 max-w-md">
      {description}
    </p>
  </div>
);

const InfoAlert = () => (
  <div className="mb-4 bg-blue-50 border-l-4 border-blue-400 p-3">
    <div className="flex">
      <Info className="w-5 h-5 text-blue-400 mr-3 mt-0.5 flex-shrink-0" />
      <div className="text-sm">
        <p className="text-blue-800 font-medium mb-1">
          Estas distancias te ayudan a calcular rutas de vehículos
        </p>
        <p className="text-blue-700">
          Configura la distancia desde un sitio base hacia todos los demás sitios del mismo estado. 
          Esto permite estimar cuántos kilómetros recorre un vehículo al trasladarse.
        </p>
      </div>
    </div>
  </div>
);

const BaseSiteIndicator = ({ baseSite, totalConnections, completedConnections }) => (
  <div className="mb-4 p-4 bg-gradient-to-r from-orange-50 to-amber-50 border border-orange-200 rounded-lg">
    <div className="flex items-center justify-between gap-6"> {/*gap-6 = separación izq/der (puedes cambiar a gap-8, gap-10, etc) */}
      
      {/* LADO IZQUIERDO */}
      <div className="flex items-center gap-4"> {/*gap-4 = separación ícono/texto (puedes cambiar a gap-5, gap-6, etc) */}
        <div className="w-10 h-10 bg-orange-500 rounded-lg flex items-center justify-center">
          <Target className="w-5 h-5 text-white" />
        </div>
        
        <div className="space-y-1"> {/*space-y-1 = separación entre líneas de texto (puedes cambiar a space-y-2, space-y-3, etc) */}
          <p className="text-xs font-medium text-orange-700 uppercase tracking-wide">
            Sitio Base (Origen)
          </p>
          <h3 className="text-lg font-semibold text-orange-900">
            {baseSite.nombre}
          </h3>
        </div>
      </div>
      
      {/* LADO DERECHO */}
      <div className="text-right space-y-2"> {/*space-y-2 = separación texto/barra (puedes cambiar a space-y-3, space-y-4, etc) */}
        <div className="text-sm text-orange-700 font-medium">
          {completedConnections} de {totalConnections} configuradas
        </div>
        
        <div className="w-32 bg-orange-200 rounded-full h-2">
          <div 
            className="bg-orange-500 h-2 rounded-full transition-all duration-300"
            style={{ width: `${totalConnections > 0 ? (completedConnections / totalConnections) * 100 : 0}%` }}
          ></div>
        </div>
      </div>
      
    </div>
  </div>
);
const SiteDistancesSection = ({ vehiculos, loading, addNotification, confirmDialog, setConfirmDialog }) => {
  const [estados, setEstados] = useState([]);
  const [sitiosPorEstado, setSitiosPorEstado] = useState({});
  const [selectedEstado, setSelectedEstado] = useState('');
  const [selectedBaseSite, setSelectedBaseSite] = useState('');
  const [distances, setDistances] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [isEstadoOpen, setIsEstadoOpen] = useState(false);
  const [isSitioOpen, setIsSitioOpen] = useState(false);
  const [estadoSearch, setEstadoSearch] = useState('');
  const [sitioSearch, setSitioSearch] = useState('');
  const [apiDistances, setApiDistances] = useState([]);      
  const [distancesLoading, setDistancesLoading] = useState(false);
  const [distanceScope, setDistanceScope] = useState('same'); // 'same' | 'cross'

  useEffect(() => {
    const loadData = async () => {
      if (!vehiculos || vehiculos.length === 0) {
        setEstados([]);
        setSitiosPorEstado({});
        setSelectedEstado('');
        setSelectedBaseSite('');
        return;
      }

      try {
        const estadosData = await getEstadosFromVehiculos(vehiculos);
        setEstados(estadosData || []);

        const sitiosMap = {};
        for (const estado of estadosData) {
          const sitios = await getSitiosByEstado(vehiculos, estado.nombre);
          sitiosMap[estado.nombre] = (sitios || [])
            .filter(s => !!s.nombre)
            .map(s => ({
              ...s,
              estado: estado.nombre,
            }));
        }

        setSitiosPorEstado(sitiosMap);
      } catch (error) {
        console.error('Error cargando estados/sitios para distancias:', error);
      }
    };

    loadData();
  }, [vehiculos]);

  const estadoOptions = useMemo(() => estados.map((e) => e.nombre), [estados]);

  const sitiosDelEstado = useMemo(() => {
    if (!selectedEstado) return [];
    return sitiosPorEstado[selectedEstado] || [];
  }, [sitiosPorEstado, selectedEstado]);

  // MOVIDO: Todos los sitios de todos los estados
  const allSitios = useMemo(
    () => Object.values(sitiosPorEstado).flat(),
    [sitiosPorEstado]
  );

  // MOVIDO: Sitios de otros estados distintos al seleccionado
  const otrosSitios = useMemo(() => {
    if (!selectedEstado) return [];
    return allSitios.filter(s => s.estado !== selectedEstado);
  }, [allSitios, selectedEstado]);

  // MOVIDO: baseSite
  const baseSite = useMemo(
    () => sitiosDelEstado.find((s) => s.nombre === selectedBaseSite) || null,
    [sitiosDelEstado, selectedBaseSite]
  );
  
  // Quita ceros a la derecha de las distancias: 200.00->200, 200.50->200.5
  const normalizeDistance = (v) => {
    if (v === null || v === undefined || v === '') return '';
    const n = typeof v === 'number' ? v : Number(String(v).replace(',', '.'));
    if (!Number.isFinite(n)) return '';
    return String(n);
  };

  useEffect(() => {
    const syncDistancesFromApi = async () => {
      if (!baseSite || allSitios.length === 0) return;

      try {
        setDistancesLoading(true);

        const data = await obtenerDistancias();
        console.log(' Distancias desde API:', data);
        setApiDistances(data || []);

        const sitioById = new Map(allSitios.map(s => [s.id, s]));
        const newDistances = {};

        data.forEach(d => {
          const sitioOrigen = sitioById.get(d.origen);
          const sitioDestino = sitioById.get(d.destino);
          if (!sitioOrigen || !sitioDestino) return;

          // Solo distancias donde participe el sitio base
          if (sitioOrigen.id !== baseSite.id && sitioDestino.id !== baseSite.id) {
            return;
          }

          const key = makePairKey(sitioOrigen.nombre, sitioDestino.nombre);
          newDistances[key] = normalizeDistance(d.distancia_km);
        });

        // No pisamos lo que el usuario haya tecleado, solo completamos huecos
        setDistances(prev => ({
          ...newDistances,
          ...prev,
        }));
      } catch (err) {
        console.error(' Error cargando distancias API:', err);
        addNotification({
          type: 'error',
          title: 'Error al cargar distancias',
          message: 'No se pudieron cargar las distancias registradas desde la API.',
          duration: 5000,
        });
      } finally {
        setDistancesLoading(false);
      }
    };

    syncDistancesFromApi();
  }, [baseSite, allSitios, addNotification]);

  const filteredEstadoOptions = useMemo(() => {
    const q = estadoSearch.toLowerCase();
    if (!q) return estadoOptions;
    return estadoOptions.filter((e) => e.toLowerCase().includes(q));
  }, [estadoOptions, estadoSearch]);

  const filteredSitiosDelEstado = useMemo(() => {
    const q = sitioSearch.toLowerCase();
    if (!q) return sitiosDelEstado;
    return sitiosDelEstado.filter((s) => s.nombre.toLowerCase().includes(q));
  }, [sitiosDelEstado, sitioSearch]);

  useEffect(() => {
    if (!selectedEstado) {
      setSelectedBaseSite('');
      return;
    }

    const sitios = sitiosDelEstado;
    if (!sitios.length) {
      setSelectedBaseSite('');
      return;
    }

    const exists = sitios.some((s) => s.nombre === selectedBaseSite);
    if (!exists) {
      setSelectedBaseSite('');
    }
  }, [sitiosDelEstado, selectedEstado, selectedBaseSite]);

  // Pares dentro del mismo estado (lo que ya tenías)
  const sameStatePairs = useMemo(() => {
    if (!baseSite) return [];

    return sitiosDelEstado
      .filter((s) => s.nombre !== baseSite.nombre)
      .map((other) => {
        const key = makePairKey(baseSite.nombre, other.nombre);
        return {
          key,
          a: baseSite,
          b: other,
        };
      });
  }, [sitiosDelEstado, baseSite]);

  // Pares hacia otros estados
  const crossStatePairs = useMemo(() => {
    if (!baseSite) return [];

    return otrosSitios.map((other) => {
      const key = makePairKey(baseSite.nombre, other.nombre);
      return {
        key,
        a: baseSite,
        b: other,
      };
    });
  }, [otrosSitios, baseSite]);

  // Pares que se muestran según el modo actual
  const sitePairs = useMemo(() => {
    if (distanceScope === 'same') {
      return sameStatePairs;
    } else {
      return crossStatePairs;
    }
  }, [distanceScope, sameStatePairs, crossStatePairs]);

  // Total de pares configurables para este sitio base (para el indicador)
  const allPairsForBaseSite = useMemo(
    () => [...sameStatePairs, ...crossStatePairs],
    [sameStatePairs, crossStatePairs]
  );  

  // Calcular distancias completadas
  const completedConnections = useMemo(() => {
    return allPairsForBaseSite.filter(pair => {
      const distance = distances[pair.key];
      return distance && distance !== '' && !isNaN(parseFloat(distance));
    }).length;
  }, [allPairsForBaseSite, distances]);

  const handleDistanceChange = (pairKey, value) => {
    setDistances((prev) => ({
      ...prev,
      [pairKey]: value,
    }));
  };

  const handleSave = useCallback(() => {
    if (!selectedEstado) {
      addNotification({
        type: 'warning',
        title: 'Advertencia',
        message: 'Debes seleccionar un estado primero',
        duration: 4000
      });
      return;
    }

    const sitioByNombre = new Map(allSitios.map((s) => [s.nombre, s]));
    const nombresValidos = new Set(allSitios.map((s) => s.nombre));
    const payload = [];

    // Solo distancias que pertenezcan a pares del sitio base actual
    const validKeys = new Set(allPairsForBaseSite.map(p => p.key));

    for (const [pairKey, rawValue] of Object.entries(distances)) {
      if (!validKeys.has(pairKey)) continue;
      if (rawValue === '' || rawValue == null) continue;

      const distancia = parseFloat(String(rawValue).replace(',', '.'));
      if (Number.isNaN(distancia)) continue;

      const [n1, n2] = pairKey.split('::');
      if (!nombresValidos.has(n1) || !nombresValidos.has(n2)) continue;

      const [sitio_a_nombre, sitio_b_nombre] = [n1, n2].sort();

      const sitioAObj = sitioByNombre.get(sitio_a_nombre);
      const sitioBObj = sitioByNombre.get(sitio_b_nombre);

      if (!sitioAObj?.id || !sitioBObj?.id) continue;

      payload.push({
        pairKey,
        sitio_a_id: sitioAObj.id,
        sitio_b_id: sitioBObj.id,
        distancia_km: distancia,
      });
    }

    if (payload.length === 0) {
      addNotification({
        type: 'warning',
        title: 'Sin datos',
        message: 'No hay distancias válidas para guardar',
        duration: 4000
      });
      return;
    }

    // 2) Construir índice de distancias existentes en la API
    //    clave canonica: "idMenor::idMayor"
    const apiIndex = new Map();
    apiDistances.forEach(d => {
      const [id1, id2] = [d.origen, d.destino].sort((a, b) => a - b);
      const key = `${id1}::${id2}`;
      apiIndex.set(key, d);
    });

    setConfirmDialog({
      isOpen: true,
      title: '¿Guardar distancias?',
      message: `Se guardarán ${payload.length} distancia(s) para el estado ${selectedEstado}.`,
      type: 'info',
      isLoading: false,
      loadingMessage: 'Guardando distancias...',
      onConfirm: async () => {
        setConfirmDialog(prev => ({ ...prev, isLoading: true }));
        setIsSaving(true);

        try {
          const operaciones = [];

          for (const item of payload) {
            const [id1, id2] = [item.sitio_a_id, item.sitio_b_id].sort((a, b) => a - b);
            const keyIds = `${id1}::${id2}`;
            const existente = apiIndex.get(keyIds);

            if (existente) {
              // 🔁 Ya existe → PATCH
              operaciones.push(
                actualizarDistancia(existente.id, {
                  origen: id1,
                  destino: id2,
                  distancia_km: item.distancia_km,
                })
              );
            } else {
              // ➕ No existe → POST
              operaciones.push(
                crearDistancia({
                  origen: id1,
                  destino: id2,
                  distancia_km: item.distancia_km,
                })
              );
            }
          }

          const results = await Promise.all(operaciones);
          console.log('✅ Resultados guardado distancias:', results);

          // Refrescar cache local de la API
          try {
            const refreshed = await obtenerDistancias();
            setApiDistances(refreshed || []);
          } catch (e) {
            console.error('No se pudo refrescar distancias luego de guardar', e);
          }

          setConfirmDialog({ isOpen: false });

          addNotification({
            type: 'success',
            title: 'Distancias guardadas',
            message: `Se guardaron ${payload.length} distancia(s) correctamente`,
            duration: 4000
          });

        } catch (err) {
          console.error('🔥 Error al guardar distancias:', err);
          setConfirmDialog({ isOpen: false });

          addNotification({
            type: 'error',
            title: 'Error al guardar',
            message: 'No se pudieron guardar las distancias. Intenta de nuevo.',
            duration: 5000
          });
        } finally {
          setIsSaving(false);
        }
      }
    });
  }, [
    selectedEstado,
    allSitios,
    distances,
    apiDistances,
    allPairsForBaseSite,
    addNotification,
    setConfirmDialog
  ]);

  const handleEstadoChange = (value) => {
    setSelectedEstado(value);
    setSelectedBaseSite('');
  };

  return (
    <section className="mb-6">
      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mb-4">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
              <Route className="w-5 h-5 text-orange-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900">
              Distancias entre sitios
            </h2>
          </div>
          {loading && (
            <div className="mt-2 inline-flex items-center gap-2 px-3 py-1 rounded-full bg-orange-50 text-orange-700 text-xs font-medium">
              <Loader2 className="w-3 h-3 animate-spin" />
              <span>Calculando sitios para trazabilidad...</span>
            </div>
          )}
        </div>

        <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center">
          {/* Selector de estado */}
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-gray-700">
              Estado:
            </label>

            <div className="relative">
              <button
                type="button"
                className="w-56 border border-gray-300 rounded-lg px-3 py-2 text-sm flex items-center justify-between gap-2 bg-white focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-orange-400 disabled:bg-gray-100 disabled:text-gray-400"
                onClick={() => {
                  if (loading || estadoOptions.length === 0) return;
                  setIsEstadoOpen((prev) => !prev);
                  setIsSitioOpen(false);
                }}
                disabled={loading || estadoOptions.length === 0}
              >
                <span className={selectedEstado ? 'text-gray-900' : 'text-gray-400'}>
                  {selectedEstado || (loading ? 'Cargando estados...' : 'Selecciona un estado')}
                </span>
                <svg
                  className={`w-4 h-4 text-gray-500 transition-transform ${
                    isEstadoOpen ? 'rotate-180' : ''
                  }`}
                  viewBox="0 0 20 20"
                  fill="none"
                >
                  <path
                    d="M5 7.5L10 12.5L15 7.5"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>

              {isEstadoOpen && !loading && estadoOptions.length > 0 && (
                <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-56 overflow-y-auto">
                  <div className="p-2 border-b border-gray-200 bg-white sticky top-0 z-10">
                    <input
                      type="text"
                      placeholder="Buscar estado..."
                      className="w-full px-2 py-1 text-xs border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-orange-400"
                      value={estadoSearch}
                      onChange={(e) => setEstadoSearch(e.target.value)}
                      autoFocus
                    />
                  </div>

                  <ul className="py-1 text-sm">
                    {filteredEstadoOptions.length === 0 && (
                      <li className="px-3 py-2 text-xs text-gray-400">
                        Sin resultados
                      </li>
                    )}

                    {filteredEstadoOptions.map((estado) => (
                      <li
                        key={estado}
                        className={`px-3 py-2 cursor-pointer hover:bg-orange-50 ${
                          estado === selectedEstado ? 'bg-orange-50 font-medium' : ''
                        }`}
                        onClick={() => {
                          handleEstadoChange(estado);
                          setIsEstadoOpen(false);
                          setEstadoSearch('');
                        }}
                      >
                        {estado}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>

          {/* Selector de sitio base */}
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-gray-700">
              Sitio base:
            </label>

            <div className="relative">
              <button
                type="button"
                className="w-64 border border-gray-300 rounded-lg px-3 py-2 text-sm flex items-center justify-between gap-2 bg-white focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-orange-400 disabled:bg-gray-100 disabled:text-gray-400"
                onClick={() => {
                  if (loading || !selectedEstado || sitiosDelEstado.length < 2) return;
                  setIsSitioOpen((prev) => !prev);
                  setIsEstadoOpen(false);
                }}
                disabled={loading || !selectedEstado || sitiosDelEstado.length < 2}
              >
                <span className={selectedBaseSite ? 'text-gray-900' : 'text-gray-400'}>
                  {selectedBaseSite
                    ? selectedBaseSite
                    : selectedEstado
                    ? 'Selecciona un sitio'
                    : 'Primero elige un estado'}
                </span>
                <svg
                  className={`w-4 h-4 text-gray-500 transition-transform ${
                    isSitioOpen ? 'rotate-180' : ''
                  }`}
                  viewBox="0 0 20 20"
                  fill="none"
                >
                  <path
                    d="M5 7.5L10 12.5L15 7.5"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>

              {isSitioOpen && !loading && selectedEstado && sitiosDelEstado.length >= 2 && (
                <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-56 overflow-y-auto">
                  <div className="p-2 border-b border-gray-200 bg-white sticky top-0 z-10">
                    <input
                      type="text"
                      placeholder="Buscar sitio..."
                      className="w-full px-2 py-1 text-xs border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-orange-400"
                      value={sitioSearch}
                      onChange={(e) => setSitioSearch(e.target.value)}
                      autoFocus
                    />
                  </div>

                  <ul className="py-1 text-sm">
                    {filteredSitiosDelEstado.length === 0 && (
                      <li className="px-3 py-2 text-xs text-gray-400">
                        Sin resultados
                      </li>
                    )}

                    {filteredSitiosDelEstado.map((sitio) => (
                      <li
                        key={sitio.nombre}
                        className={`px-3 py-2 cursor-pointer hover:bg-orange-50 ${
                          sitio.nombre === selectedBaseSite ? 'bg-orange-50 font-medium' : ''
                        }`}
                        onClick={() => {
                          setSelectedBaseSite(sitio.nombre);
                          setIsSitioOpen(false);
                          setSitioSearch('');
                        }}
                      >
                        {sitio.nombre}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <InfoAlert />

      {!loading && !selectedEstado && (
        <EmptyStateMessage
          icon={Search}
          title="Selecciona un estado para empezar"
          description="Usa los selectores de arriba para elegir el estado y luego define las distancias entre sus sitios."
        />
      )}

      {!loading && selectedEstado && sitiosDelEstado.length < 2 && (
        <EmptyStateMessage
          icon={MapPin}
          title="No hay suficientes sitios en este estado"
          description="En este estado hay menos de 2 sitios registrados, por lo que no es necesario capturar distancias."
        />
      )}

      {!loading && selectedEstado && sitiosDelEstado.length >= 2 && !baseSite && (
        <EmptyStateMessage
          icon={MapPin}
          title="Selecciona un sitio base"
          description="Elige un sitio base en el selector de arriba para capturar la distancia hacia el resto de los sitios del estado."
        />
      )}

     {/* Tabla mejorada: cuando ya hay sitio base */}
{!loading && selectedEstado && baseSite && allPairsForBaseSite.length > 0 && (
  <>
    {/* Indicador + toggle de alcance */}
    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-3">
      <BaseSiteIndicator 
        baseSite={baseSite} 
        totalConnections={allPairsForBaseSite.length}
        completedConnections={completedConnections}
      />

      {/* Toggle de alcance de distancias */}
      <div className="inline-flex rounded-full border border-gray-200 bg-white p-1 text-xs font-medium">
        <button
          type="button"
          onClick={() => setDistanceScope('same')}
          className={`px-3 py-1 rounded-full transition-colors ${
            distanceScope === 'same'
              ? 'bg-orange-500 text-white shadow-sm'
              : 'text-gray-600 hover:bg-gray-50'
          }`}
        >
          Dentro del estado
        </button>
        <button
          type="button"
          onClick={() => setDistanceScope('cross')}
          className={`px-3 py-1 rounded-full transition-colors ${
            distanceScope === 'cross'
              ? 'bg-orange-500 text-white shadow-sm'
              : 'text-gray-600 hover:bg-gray-50'
          }`}
        >
          Hacia otros estados
        </button>
      </div>
    </div>

    {/* La tabla se queda igual, solo asegurarte de usar `sitePairs` */}
    <div className="overflow-x-auto border border-gray-200 rounded-lg bg-white shadow-sm">
      <table className="min-w-full text-sm">
        <thead className="bg-gray-50 border-b border-gray-200">
          <tr>
            <th className="px-4 py-3 text-left font-semibold text-gray-700">
              {distanceScope === 'same'
                ? 'Sitio destino'
                : 'Sitio destino (otros estados)'}
            </th>
            <th className="px-4 py-3 text-left font-semibold text-gray-700">
              Distancia desde{' '}
              <span className="text-orange-600 font-bold">
                {baseSite.nombre}
              </span>
            </th>
            <th className="px-4 py-3 text-center font-semibold text-gray-700 w-24">
              Estado
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {sitePairs.map(({ key, b }) => {
            const hasValue = distances[key] && !isNaN(parseFloat(distances[key]));
            
            return (
              <tr key={key} className="hover:bg-gray-50 transition-colors">
                <td className="px-4 py-3">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center">
                      <MapPin className="w-4 h-4 text-gray-600" />
                    </div>
                    <div className="flex flex-col">
                      <span className="font-medium text-gray-900">{b.nombre}</span>
                      {distanceScope === 'cross' && (
                        <span className="text-xs text-gray-500">
                          {b.estado}
                        </span>
                      )}
                    </div>
                  </div>
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center">
                      <ArrowRight className="w-4 h-4 text-orange-600" />
                    </div>
                    <input
                      type="number"
                      step="0.1"
                      min="0"
                      className="w-28 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-orange-400"
                      value={distances[key] ?? ''}
                      onChange={(e) => handleDistanceChange(key, e.target.value)}
                      placeholder="0.0"
                    />
                    <span className="text-sm text-gray-500 font-medium">km</span>
                  </div>
                </td>
                <td className="px-4 py-3 text-center">
                  {hasValue ? (
                    <div className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
                      <CheckCircle2 className="w-3 h-3" />
                      Listo
                    </div>
                  ) : (
                    <div className="inline-flex items-center px-2 py-1 bg-gray-100 text-gray-600 rounded-full text-xs">
                      Pendiente
                    </div>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>

    <div className="mt-4 flex justify-end">
      <button
        type="button"
        className="inline-flex items-center justify-center px-4 py-2 text-sm font-medium rounded-lg bg-orange-500 text-white hover:bg-orange-600 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
        onClick={handleSave}
        disabled={isSaving || !selectedEstado || !baseSite || sitePairs.length === 0}
      >
        {isSaving ? (
          <>
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            Guardando...
          </>
        ) : (
          'Guardar distancias'
        )}
      </button>
    </div>
  </>
)}
</section>
  );
};

export default SiteDistancesSection;